import React, { useCallback, useEffect, useState } from "react"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import moment from "moment"
import { useSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"
import { UseConfirmDialog } from "components/customHooks"
import { createDsarRequest, listDsarRequests } from "services/memberDsar"
import { verifyResponse } from "utils/httpRequest"
import { StyledMemberGdpr } from "./Member.style"

const statusColor = {
  pending: "warning",
  approved: "info",
  in_progress: "primary",
  completed: "success",
  failed: "error",
  cancelled: "default",
}

const MemberGdpr = ({ member, access }) => {
  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState([])
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "GDPR / DSAR Action",
    description: "Create a DSAR erasure request for this member?",
    agreeText: "Yes",
    disagreeText: "No",
  })

  const loadRequests = useCallback(async () => {
    if (!access?.memberDsarListAccess || !member?.email) return
    setLoading(true)
    try {
      const response = await listDsarRequests({
        pageNo: 1,
        pageSize: 10,
        searchEmail: member.email,
        requestType: "erase",
      })
      verifyResponse(response)
      setRequests(response.data?.list || [])
    } finally {
      setLoading(false)
    }
  }, [access?.memberDsarListAccess, member?.email])

  useEffect(() => {
    loadRequests()
  }, [loadRequests])

  const createErasureRequest = async () => {
    setLoading(true)
    try {
      const response = await createDsarRequest({
        memberId: member.userId,
        memberEmail: member.email,
        requestType: "erase",
        requestSource: "cms-member-gdpr-tab",
        requestNotes: "Created from member GDPR / DSAR tab.",
      })
      verifyResponse(response)
      enqueueSnackbar("DSAR erasure request created.", { variant: "success" })
      await loadRequests()
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  return (
    <StyledMemberGdpr>
      <Card className="section-card">
        <CardContent>
          <div className="section-heading">
            <div>
              <h3 className="section-title">GDPR / DSAR Erasure</h3>
              <Typography className="section-copy">
                Use this section for verified privacy deletion requests. This creates an
                auditable request; approval and execution happen in the DSAR / Erase queue.
              </Typography>
            </div>
            <Button variant="outlined" onClick={() => navigate("/member-dsar")}>
              Open DSAR Queue
            </Button>
          </div>

          <div className="action-row">
            {access?.memberDsarCreateAccess ? (
              <Button
                variant="contained"
                color="error"
                disabled={loading}
                onClick={() => openConfirmDialog(createErasureRequest)}
              >
                Create Erasure Request
              </Button>
            ) : (
              <Typography className="permission-note">
                You do not have permission to create DSAR requests.
              </Typography>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="section-card">
        <CardContent>
          <h3 className="section-title">Request History</h3>
          {!access?.memberDsarListAccess && (
            <Typography className="permission-note">
              You do not have permission to view DSAR request history.
            </Typography>
          )}
          {access?.memberDsarListAccess && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell>Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="empty-cell">
                        No DSAR requests found for this member.
                      </TableCell>
                    </TableRow>
                  )}
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{request.id}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={request.requestStatus}
                          color={statusColor[request.requestStatus] || "default"}
                        />
                      </TableCell>
                      <TableCell>{request.createdAt ? moment(request.createdAt).format("lll") : "-"}</TableCell>
                      <TableCell>{request.dueAt ? moment(request.dueAt).format("ll") : "-"}</TableCell>
                      <TableCell>{request.completedAt ? moment(request.completedAt).format("lll") : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {ConfirmDialog}
    </StyledMemberGdpr>
  )
}

export default MemberGdpr
